<template>
  <UiModal v-model="isQnaModalOpen" :title="t('common.qnaBoard.title')" size="large">
    <div class="qna-board-modal">
      <div class="qna-board-search flex align-center gap-10 flex-wrap">
        <div class="flex align-center gap-5">
          <label for="qnaSearchKeyword">{{ t('common.noticeBoard.subject') }}</label>
          <UiInput
            id="qnaSearchKeyword"
            v-model="qnaFind.searchKeyword"
            class="qna-board-search-input"
            @keyup.enter="handleQnaSearch"
          />
        </div>
        <div class="flex align-center gap-5">
          <label for="qnaUserNm">{{ t('common.qnaBoard.author') }}</label>
          <UiInput id="qnaUserNm" v-model="qnaFind.userNm" class="qna-board-search-input--short" @keyup.enter="handleQnaSearch" />
        </div>
        <UiButton variant="primary" @click="handleQnaSearch">{{ t('common.action.search') }}</UiButton>
      </div>

      <UiTableDefault v-model="qnaList" :column-count="5" class="qna-board-table" hover striped>
        <template #header>
          <tr>
            <th class="txt-c" style="width: 60px">{{ t('common.noticeBoard.number') }}</th>
            <th>{{ t('common.noticeBoard.subject') }}</th>
            <th class="txt-c" style="width: 100px">{{ t('common.qnaBoard.author') }}</th>
            <th class="txt-c" style="width: 70px">{{ t('common.qnaBoard.views') }}</th>
            <th class="txt-c" style="width: 120px">{{ t('common.noticeBoard.registeredDate') }}</th>
          </tr>
        </template>
        <template #body="{ rows }">
          <tr v-for="item in rows" :key="item.seq">
            <td class="txt-c">{{ item.groupOrder == 0 ? item.groupSeq : '' }}</td>
            <td>
              <div class="qna-board-title" :style="{ paddingLeft: `${(item.groupLevel || 0) * 16}px` }">
                <a href="#none" class="qna-board-title-link" @click.prevent="handleQnaDetail(item)">
                  {{ item.title }}
                  <span v-if="item.commentCnt > 0"> [{{ item.commentCnt }}]</span>
                </a>
              </div>
            </td>
            <td class="txt-c">{{ item.userNm }}</td>
            <td class="txt-c">{{ item.hit }}</td>
            <td class="txt-c">{{ item.createDt }}</td>
          </tr>
        </template>
        <template #empty>
          <tr>
            <td colspan="5" class="txt-c">{{ t('common.qnaBoard.empty') }}</td>
          </tr>
        </template>
      </UiTableDefault>

      <UiPaging
        v-if="qnaRecords > 0"
        :current-page="qnaFind.page"
        :total-items="qnaRecords"
        :items-per-page="qnaFind.rows"
        @update:current-page="handleQnaPageChange"
      />
    </div>
  </UiModal>

  <UiModal
    v-model="isQnaDetailModalOpen"
    :title="t('common.qnaBoard.detailTitle')"
    size="medium"
    @update:model-value="onDetailModalChange"
  >
    <div v-if="qnaDetail" class="qna-board-detail">
      <div class="qna-board-detail__meta flex justify-between gap-10">
        <strong>{{ qnaDetail.title }}</strong>
        <span>{{ qnaDetail.createDt }}</span>
      </div>
      <div class="qna-board-detail__info flex gap-15">
        <span>{{ t('common.qnaBoard.authorLabel', { name: qnaDetail.userNm }) }}</span>
        <span>{{ t('common.qnaBoard.viewsLabel', { count: qnaDetail.hit }) }}</span>
      </div>
      <table class="qna-board-detail__table">
        <colgroup>
          <col style="width: 20%" />
          <col style="width: 80%" />
        </colgroup>
        <tbody>
          <tr>
            <th>{{ t('common.noticeBoard.subject') }}</th>
            <td>{{ qnaDetail.title }}</td>
          </tr>
          <tr>
            <th>{{ t('common.noticeBoard.content') }}</th>
            <td class="qna-board-detail__content">{{ qnaDetail.content }}</td>
          </tr>
          <tr>
            <th>{{ t('common.attachments') }}</th>
            <td>
              <UiFileAttachment v-model="qnaAttachmentFiles" :isFileUploadDisabled="true" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </UiModal>
</template>

<script setup>
import UiFileAttachment from '~/components/attachment/UiFileAttachment.vue';
import { useQnaBoardStore } from '~/composables/user/useQnaBoardStore';

const { t } = useI18n();

const {
  isQnaModalOpen,
  isQnaDetailModalOpen,
  qnaList,
  qnaRecords,
  qnaFind,
  qnaDetail,
  qnaAttachmentFiles,
  handleQnaSearch,
  handleQnaPageChange,
  handleQnaDetail,
  closeQnaDetail,
} = useQnaBoardStore();

const onDetailModalChange = open => {
  if (!open) closeQnaDetail();
};
</script>

<style lang="scss" scoped>
.qna-board-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.qna-board-search {
  padding-top: 4px;

  label {
    white-space: nowrap;
    font-size: $font-size-sm;
  }
}

.qna-board-search-input {
  width: 200px;
}

.qna-board-search-input--short {
  width: 120px;
}

.qna-board-title-link {
  color: $text-color;
  text-decoration: none;

  &:hover {
    color: $primary-color;
    text-decoration: underline;
  }
}

.qna-board-detail {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__meta {
    padding-bottom: 8px;
    border-bottom: 1px solid $border-color;
  }

  &__info {
    font-size: $font-size-sm;
    color: #666;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: $font-size-sm;

    th,
    td {
      border: 1px solid $border-color;
      padding: 10px 12px;
      vertical-align: top;
    }

    th {
      background: #f8f9fa;
      text-align: left;
    }
  }

  &__content {
    white-space: pre-wrap;
    word-break: break-word;
  }
}
</style>
